import React, { useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { Content, Header, Page } from '@backstage/core-components';
import { Box, Button, TextField, Card, CardContent, CardHeader, Divider, Backdrop, CircularProgress, Grid } from '@material-ui/core';
import { useRequestService } from '../../services/RequestService';
import { Component } from './types';
import { useLocation, useNavigate } from 'react-router';
import { AlertMessage } from '../../components/alert';
import { AutocompleteInput, SelectInput } from '../../components/input';
import { z } from 'zod';
import { Autocomplete } from '@material-ui/lab';
import { useFormDefinition } from './useFormDefinition';
import { startCase } from 'lodash';

const componentSchema = z.object({
    type: z.string().nonempty('Type is required'),
    system: z.string().nonempty('System is required'),
    name: z.string().nonempty('Name is required')
        .max(63, 'Identifier must be 63 characters or less')
        .regex(/^[a-zA-Z0-9-]+$/, 'Only letters, numbers, and hyphens are allowed.')
        .refine(val => !/^(supply-|sl-)/i.test(val), {
            message: 'Identifier cannot start with supply- or sl-',
        }),
    title: z.string().nonempty('Title is required'),
    description: z.string().nonempty('Description is required').max(120, 'Description must be 120 characters or less'),
    tags: z.array(z.string()).optional(),
});

const initialValues: Component = {
    type: '',
    system: '',
    name: '',
    title: '',
    description: '',
    tags: [],
};

const getSuggestedNameAndTitle = (system: string) => {
    if (!system) return { };

    const systemName = `${system.toLowerCase().replace(/^(.*?)\//, '')}`;
    return {
        name: `${systemName}-webapp`,
        title: `${startCase(systemName)} - Webapp`,
    };
};

export const NewComponentPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const requestService = useRequestService();
    const { 
        formValues, 
        setFormValue, 
        setFormValues, 
        isFormValid, 
        getFieldProps,
        checkNameAvailability
    } = useFormDefinition(
        componentSchema, 
        {...initialValues, system: (state?.owner as string) || ''},
        {checkNameAvailability: { fieldName: 'name', requestType: 'component' }}
    );
    
    const componentTypes = [
        { value: 'service', label: 'Service (e.g.: SpringBoot application)' },
        { value: 'website', label: 'Website (e.g.: Angular or React application)' },
        // { value: 'library', label: 'Library (e.g.: Java or Typescript SDK/Dependency)' }, // not supported yet
    ];
    const { value: systems } = useAsync(async () => await requestService.listSystems(), []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (field: string, value: any) => {
        setFormValue(field, value);
    };

    const handleSystemChange = (field: string, value: any) => {
        if (formValues.type === 'website') {
            const nameAndTitleSuggestion = getSuggestedNameAndTitle(value);
            setFormValues(prev => ({
                ...prev,
                [field]: value,
                ...nameAndTitleSuggestion,
            }));
            checkNameAvailability(nameAndTitleSuggestion.name, value);
        } else {
            handleChange(field, value);
            checkNameAvailability(formValues.name, value);
        }
    };

    const handleTypeChange = (value: string) => {
        switch (value) {
            case 'service':
                setFormValues(prev => ({
                    ...prev,
                    type: value,
                    name: '',
                    title: '',
                    tags: ['java', 'springboot'],
                }));
                break;
            case 'library':
                setFormValues(prev => ({
                    ...prev,
                    type: value,
                    tags: ['java', 'typescript'],
                }));
                break;
            case 'website':
                setFormValues(prev => ({
                    ...prev,
                    type: value,
                    tags: ['angular', 'typescript'],
                    ...getSuggestedNameAndTitle(formValues.system),
                }));
                break;
            default:
                setFormValues(prev => ({
                    ...prev,
                    type: value,
                    tags: [],
                }));
                break;
        }
    }

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await requestService.createComponent(formValues);
            navigate('/requests', { state: { message: 'Component request created successfully!' } });
        } catch (err) {
            setError('Failed to submit component request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Page themeId="home">
            <Header title="New Component" type="New request" typeLink='/requests/new-request' />
            <Content>
                <Card>
                    <CardHeader title="Create a new component" subheader="Fill out the form below to create a new component request. Once submitted, you will be able to track the status of your request on the requests page." />
                    <CardContent>
                        <Divider />
                        <Box
                            component="form"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ gap: '16px' }}
                            mt={3}
                        >
                            <Box width="100%" maxWidth="800px">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <SelectInput
                                            label="Type"
                                            value={formValues.type}
                                            options={componentTypes}
                                            onChange={handleTypeChange}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <AutocompleteInput
                                            label="System"
                                            fieldName="system"
                                            formValues={formValues}
                                            options={systems || []}
                                            onChange={handleSystemChange}
                                            disabled={!!state?.owner}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Identifier"
                                            placeholder='e.g.: service, website'
                                            variant="outlined"
                                            value={formValues.name}
                                            onChange={event => handleChange('name', event.target.value)}
                                            onBlur={event => checkNameAvailability(event.target.value, formValues.system)}
                                            required
                                            fullWidth
                                            {...getFieldProps('name')}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Friendly Name"
                                            placeholder='e.g.: Supply Logistica Produto Service, Supply Engenharia Control Frontend'
                                            variant="outlined"
                                            value={formValues.title}
                                            onChange={event => handleChange('title', event.target.value)}
                                            required
                                            fullWidth
                                            {...getFieldProps('title')}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Description"
                                            variant="outlined"
                                            value={formValues.description}
                                            onChange={event => handleChange('description', event.target.value)}
                                            required
                                            fullWidth
                                            inputProps={{ maxLength: 120 }}
                                            helperText={`${formValues.description.length}/120`}
                                            {...getFieldProps('description')}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Autocomplete
                                            multiple
                                            freeSolo
                                            options={[]}
                                            value={formValues.tags}
                                            onChange={(_, newValue) => handleChange('tags', newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Tags"
                                                    placeholder="e.g.: java, springboot, angular (press enter to add)"
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3} width="100%" maxWidth="800px">
                                <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" color="primary">
                                    Submit
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Content>

            <Backdrop open={loading} style={{ zIndex: 1300 }}>
                <CircularProgress color="primary" />
            </Backdrop>

            {error && <AlertMessage message={error} />}
        </Page>
    );
};
